import React from "react";
import styled, { css } from "styled-components";
import selectArrows from "assets/select-arrows.svg";
import selectArrowsActive from "assets/select-arrows-active.svg";
import selectArrowsError from "assets/select-arrows-error.svg";
import IconCheckBlue from "assets/icon-check-blue.svg";

export const FormHeading = styled.div`
  color: ${p => p.theme.text};
  font-size: 17px;
  margin-bottom: 20px;
  padding-bottom: 4px;
`;

export const FormErrors = styled.p`
  color: ${p => p.theme.error};
  font-weight: 600;
  padding-top: 10px;
`;

export const Rule = styled.hr`
  border: none;
  border-top: ${p => p.theme.TextInput.border};
  margin: 20px 0;
`;

export const Label = styled.label`
  color: ${p => p.theme.Label.color};
  font-weight: 400;
  input:not([type="checkbox"]):not([type="radio"]),
  select,
  textarea {
    margin-top: 10px;
  }
`;

export const TextInputLabel = styled(Label)`
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 10px;
  text-align: left;
  small {
    display: block;
    font-size: 11px;
    font-weight: 400;
    opacity: 0.75;
    margin-top: 3px;
  }
`;

export const InputLabel = styled(TextInputLabel)`
  margin-bottom: ${p => p.theme.padding};
`;

export const RadioLabel = styled(TextInputLabel)`
  align-items: center;
  display: inline-flex;
  margin-right: 10px;
  vertical-align: middle;
  input {
    margin-left: 5px;
    vertical-align: middle;
  }
`;

export const CheckboxLabel = styled(RadioLabel)`
  margin: auto;
  cursor: pointer;

  input {
    margin: 0 8px 0 0;
  }
`;

export const ToggleSwitchLabel = RadioLabel;

const inputStylesMixin = css`
  background-color: ${p => p.theme.TextInput.backgroundColor || "white"};
  border-radius: 0;
  border: ${p => p.theme.TextInput.border};
  font-family: ${p => p.theme.fontFamily};
  font-size: 13px;
  font-weight: 400;
  outline: 0;
  width: 100%;
  :focus:not(:read-only) {
    border-color: ${p => p.theme.TextInput.focus.borderColor};
  }
  &.error {
    color: ${p => p.theme.error};
    border-color: ${p => p.theme.error};
    :focus:not(:read-only) {
      border-color: ${p => p.theme.error};
    }
  }

  :read-only {
    background-color: ${p => p.theme.TextInput.inactive};
  }

  &::placeholder {
    color: ${p => p.theme.TextInput.placeholder};
  }
`;

export const TextInput = styled.input`
  ${inputStylesMixin};
  height: 35px;
  line-height: 35px;
  padding: 0 10px;
`;

export const TextareaInput = styled.textarea`
  ${inputStylesMixin};
  overflow: auto;
  height: 75px;
  line-height: 17px;
  padding: 10px;
`;

export const RadioInputLabel = styled(TextInputLabel)`
  cursor: pointer;
  line-height: 1.7;
  padding-bottom: 0;
  p {
    font-weight: 400;
    margin: 0;
    opacity: 0.75;
  }
`;

export const Radio = styled(TextInput).attrs({
  type: "radio",
})`
  align-items: center;
  appearance: none;
  border-radius: 100%;
  cursor: pointer;
  display: inline-flex;
  height: ${p => p.theme.Checkbox.size}px;
  justify-content: center;
  margin: 0 5px 0 0;
  padding: 0;
  position: relative;
  vertical-align: middle;
  width: ${p => p.theme.Checkbox.size}px;
  &:after {
    background-color: ${p => p.theme.Radio.checked.backgroundColor};
    border-radius: 100%;
    content: "";
    display: none;
    height: ${p => Math.floor(p.theme.Checkbox.size * 0.6)}px;
    width: ${p => Math.floor(p.theme.Checkbox.size * 0.6)}px;
  }
  &:checked:after {
    display: block;
  }
`;

export const RadioInputGroup = styled.div`
  margin-bottom: 15px;
  padding-left: 30px;
  position: relative;
  font-size: 14px;
  ${Radio} {
    left: 0;
    position: absolute;
    top: 0;
  }

  &:last-of-type {
    margin-bottom: 0;

    label {
      margin-bottom: 0;
    }
  }
`;

export const CheckboxInputGroup = styled.div`
  margin-top: 5px;
`;

export const Checkbox = styled(TextInput).attrs({
  type: "checkbox",
})`
  appearance: none;
  border-radius: ${p => p.theme.Checkbox.borderRadius || 0};
  cursor: pointer;
  margin: 0 5px 0 0;
  padding: 0;
  vertical-align: middle;
  width: ${p => p.theme.Checkbox.size}px;
  height: ${p => p.theme.Checkbox.size}px;
  &:focus {
    border-color: ${p => p.theme.Checkbox.focus.borderColor};
  }
  &:checked {
    border-color: ${p => p.theme.Checkbox.checked.borderColor};
    background-image: url(${IconCheckBlue});
  }
  &.error {
    border-color: ${p => p.theme.error};
  }
`;

export const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const SelectInput = TextInput.withComponent("select");
const Sel = styled(SelectInput)`
  appearance: none;
  background-image: url(${selectArrows});
  background-position: top 50% right 15px;
  background-repeat: no-repeat;
  background-size: 13px 17px;
  padding-right: 30px;
  &:focus {
    background-image: url(${selectArrowsActive});
  }
  &.error {
    border-color: ${p => p.theme.error};
    background-image: url(${selectArrowsError});
  }
  &::-ms-expand {
    display: none;
  }
`;

/*
  There are multiple ways to use this <Select> component.
  If you provide <option> children, they will flow through as usual.
  Alternatively, you can provide an `options` property, and <option>
  children will get generated automatically.
  Any additional props are passed through to the <select>.
  If you provide both children and `options`, the generated options will
  follow the children (so e.g. you can provide a default/placeholder option).

  `options` should be an array (though an object will be accepted and converted
  to an array as described below). Each item in the array can be either just a
  raw value or an object specifying `value` and (optionally) a `disabled` flag,
  a `display` value, and a `key` for the generated <option> element.

  If `options` is an object, it will be converted to an array of objects
  mapping each k,v to { value: k, ...v } if v is an object or to
  { value: k, display: v } if v is just a display value.
  Note that the order of options is not guaranteed when using an object.

  Some examples:
  `<Select options={["Seconds", "Minutes", "Hours", "Days", "Weeks"]} />`
  will generate this:
    <select>
      <option value="Seconds">Seconds</option>
      <option value="Minutes">Minutes</option>
      <option value="Hours">Hours</option>
      <option value="Days">Days</option>
      <option value="Weeks">Weeks</option>
    </select>

  `<Select options={{ b: "Bee", c: "Sea", i: "Eye" }} />`
  will generate this:
    <select>
      <option value="b">Bee</option>
      <option value="c">Sea</option>
      <option value="i">Eye</option>
    </select>

  `<Select
    options={[
      { value: "b", display: "Bee" },
      { value: "c", display: "Sea", disabled: true },
      "i",
    ]}
  >
    <option disabled>Choose one</option>
  </Select>`
  will generate this:
    <select>
      <option disabled>Choose one</option>
      <option value="b">Bee</option>
      <option value="c" disabled>Sea</option>
      <option value="i">i</option>
    </select>
*/
export const Select = ({ options, children, ...props }) => {
  const optsArray =
    typeof options === "object" &&
    (Array.isArray(options)
      ? options.map(opt => (typeof opt === "object" ? opt : { value: opt }))
      : Object.keys(options).map(value => {
          const opt = options[value];
          const optObject = typeof opt === "object" ? opt : { display: opt };
          return { value, ...optObject };
        }));
  return (
    <Sel {...props}>
      {children}
      {optsArray &&
        optsArray.map(opt => {
          const { disabled = false, display, key, value } = opt;
          return (
            <option key={key || value} value={key || value} disabled={disabled}>
              {display || value}
            </option>
          );
        })}
    </Sel>
  );
};

const ToggleSwitch = styled.label`
  ${ToggleSwitchLabel} & {
    margin: 0 5px;
  }

  input {
    display: none;
  }

  input:checked + .check {
    background-color: ${p => p.theme.ToggleSwitch.checked.backgroundColor};

    &:after {
      left: 100%;
      transform: translateX(-100%);
    }
  }

  .check {
    background-color: ${p => p.theme.ToggleSwitch.backgroundColor};
    border-radius: ${p => p.theme.ToggleSwitch.size * 2}px;
    box-sizing: border-box;
    cursor: pointer;
    display: inline-block;
    height: ${p => p.theme.ToggleSwitch.size}px;
    padding: ${p => p.theme.ToggleSwitch.padding}px;
    transition: background-color 0.2s;
    width: ${p =>
      p.theme.ToggleSwitch.size * 2 - p.theme.ToggleSwitch.padding * 2}px;

    &:after {
      background: ${p => p.theme.ToggleSwitch.color};
      border-radius: 100%;
      content: "";
      display: block;
      height: ${p =>
        p.theme.ToggleSwitch.size - p.theme.ToggleSwitch.padding * 2}px;
      left: 0;
      position: relative;
      transition: all 0.2s;
      width: ${p =>
        p.theme.ToggleSwitch.size - p.theme.ToggleSwitch.padding * 2}px;
    }
  }
`;

export const Toggle = props => {
  const { onToggle, checked } = props;
  return (
    <ToggleSwitch>
      <input
        htmlFor="check"
        type="checkbox"
        onChange={e => onToggle(e.target.checked)}
        checked={checked}
        readOnly
      />
      <span className="check" />
    </ToggleSwitch>
  );
};

export const Info = styled.p`
  line-height: 1.5;
  font-size: 13px;
  margin: 0 0 20px 0;
  opacity: 0.75;
`;

export function onInputChange({ target }) {
  let value;
  switch (target.type) {
    case "checkbox":
      value = target.checked;
      break;
    case "number":
      value = parseFloat(target.value);
      break;
    default:
      value = target.value;
  }

  this.setState({
    [target.name]: value,
  });
}
